<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Api from 'dashboard/api/customers';

const emit = defineEmits(['saved']);
const { t } = useI18n();
const dialog = ref(null);
const customer = ref(null);
const date = ref('');
const confirmed = ref(false);
const preview = ref(null);
const error = ref('');
const busy = ref(false);
const { run, isPending, abort } = useAbortableRequest();
const refresh = async () => {
  abort();
  preview.value = null;
  if (!customer.value || !date.value) return;
  try {
    const response = await run(signal =>
      Api.preview(customer.value.id, date.value, signal)
    );
    if (response) preview.value = response.data;
  } catch (e) {
    error.value = e.response?.data?.error || t('CUSTOMERS.ERROR');
  }
};
watch(date, () => {
  error.value = '';
  confirmed.value = false;
  refresh();
});
const open = value => {
  customer.value = value;
  error.value = '';
  confirmed.value = false;
  const nextDate = value.default_paid_expires_on;
  if (date.value === nextDate) refresh();
  else date.value = nextDate;
  dialog.value.open();
};
const save = async () => {
  if (!confirmed.value || !preview.value || isPending.value) return;
  busy.value = true;
  try {
    const response = await Api.qualify(customer.value.id, {
      paid_expires_on: date.value,
      payment_confirmed: true,
      preview_token: preview.value.preview_token,
    });
    emit('saved', response.data);
    dialog.value.close();
  } catch (e) {
    error.value = e.response?.data?.error || t('CUSTOMERS.ERROR');
    confirmed.value = false;
    await refresh();
  } finally {
    busy.value = false;
  }
};
defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('CUSTOMERS.QUALIFY')"
    :description="t('CUSTOMERS.QUALIFY_DESCRIPTION')"
    :confirm-button-label="t('CUSTOMERS.QUALIFY_CONFIRM')"
    :is-loading="busy"
    :disable-confirm-button="busy || isPending || !preview || !confirmed"
    @confirm="save"
    @close="abort"
  >
    <div class="flex flex-col gap-5">
      <p v-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
      <Input
        v-model="date"
        type="date"
        :disabled="busy"
        :label="t('CUSTOMERS.PAID_EXPIRY')"
      />
      <div
        v-if="preview"
        class="rounded-xl border border-n-weak bg-n-surface-1 p-4"
      >
        <div v-for="role in ['referrer', 'customer']" :key="role" class="py-2">
          <p class="font-medium">
            {{ preview[role].name }} · {{ t('CUSTOMERS.MONTHS', { count: 1 }) }}
          </p>
          <p class="mt-1 text-n-slate-11">
            {{ preview[role].previous_expires_on }} →
            {{ preview[role].expires_on }}
          </p>
        </div>
      </div>
      <label class="flex items-start gap-3"
        ><input
          v-model="confirmed"
          type="checkbox"
          class="mt-1"
          :disabled="busy || isPending || !preview"
        /><span>{{ t('CUSTOMERS.PAYMENT_CHECK') }}</span></label
      >
      <p class="text-sm text-n-slate-11">
        {{ t('CUSTOMERS.MANUAL_EXTENSION') }}
      </p>
    </div>
  </Dialog>
</template>
